import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Barcode, ArrowLeft, ClipboardText, Truck, CheckCircle, MinusCircle,
  PlusCircle, X, Warning, ArrowsClockwise, Camera, Trash, EnvelopeOpen,
  Package,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import ScannerScanner from './ScannerScanner';
import { scanFeedback, scanCommitFeedback } from './scannerApi';

const API_ROOT = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;
const YEN = new Intl.NumberFormat('ja-JP');

/**
 * Mobile-first PO 検収 (goods-receiving) screen — v2 polish.
 *
 * New in v2:
 *   • Wider barcode format + continuous autofocus (see ScannerScanner.js).
 *   • Per-line photo (proof of condition / damage evidence) — uploads to
 *     /api/shizai/receipts/upload-photo and attaches photo_url to the receive
 *     payload.
 *   • Per-line discrepancy note when the received qty is short of remaining.
 *   • Post-submit discrepancy modal — lists short-received lines + a one-click
 *     mailto: draft to the supplier ("仕入先に連絡").
 */
export default function POReceiving() {
  const navigate = useNavigate();
  const [orderNo, setOrderNo] = useState('');
  const [order, setOrder] = useState(null);
  // line_index → { qty, photo_url, filename, discrepancy_note, uploading }
  const [lineState, setLineState] = useState({});
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  const [discrepancy, setDiscrepancy] = useState(null); // { order, discrepancies }
  const inputRef = useRef(null);
  const fileRefs = useRef({});

  useEffect(() => { inputRef.current?.focus(); }, []);

  const clear = useCallback(() => {
    setOrder(null);
    setLineState({});
    setOrderNo('');
    setNote('');
    setTimeout(() => inputRef.current?.focus(), 50);
  }, []);

  const lookup = useCallback(async (code) => {
    const trimmed = String(code || '').trim();
    if (!trimmed) return;
    setBusy(true);
    try {
      const r = await axios.get(`${API_ROOT}/shizai/lookup`, {
        params: { order_no: trimmed },
      });
      setOrder(r.data);
      // Pre-fill: qty=0 per line, no photo, no note
      const initial = {};
      (r.data.lines || []).forEach((_, i) => {
        initial[i] = { qty: 0, photo_url: '', filename: '', discrepancy_note: '', uploading: false };
      });
      setLineState(initial);
      // PO scan resolves to a receiving order — use the 入庫 (stock_in) pattern.
      scanFeedback(true, 'stock_in');
    } catch (e) {
      scanFeedback(false);
      toast.error(e?.response?.data?.detail || '該当する発注が見つかりません');
    } finally { setBusy(false); }
  }, []);

  const onDetected = useCallback(async (code) => {
    setOrderNo(code);
    await lookup(code);
  }, [lookup]);

  const patchLine = (i, patch) => setLineState((prev) => ({ ...prev, [i]: { ...prev[i], ...patch } }));

  const setLineQty = (i, val) => {
    const line = order?.lines?.[i];
    if (!line) return;
    const remaining = Math.max(0, (line.qty || 0) - (line.received_qty || 0));
    const clamped = Math.max(0, Math.min(remaining, Number(val) || 0));
    patchLine(i, { qty: clamped });
  };

  const fillRemaining = (i) => {
    const line = order?.lines?.[i];
    if (!line) return;
    const remaining = Math.max(0, (line.qty || 0) - (line.received_qty || 0));
    patchLine(i, { qty: remaining });
  };

  const fillAllRemaining = () => {
    const next = { ...lineState };
    (order?.lines || []).forEach((line, i) => {
      const remaining = Math.max(0, (line.qty || 0) - (line.received_qty || 0));
      next[i] = { ...(next[i] || {}), qty: remaining };
    });
    setLineState(next);
  };

  const uploadPhoto = async (i, file) => {
    if (!file) return;
    patchLine(i, { uploading: true });
    try {
      const { lanUpload } = await import('@/lib/lanStorage');
      const legacyUpload = (f) => {
        const fd = new FormData();
        fd.append('file', f);
        return axios.post(`${API_ROOT}/shizai/receipts/upload-photo`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        }).then((r) => r.data);
      };
      const r = await lanUpload({
        axios, apiBase: API_ROOT, kind: 'shizai_receipt',
        file, legacyUpload,
      });
      patchLine(i, { photo_url: r.photo_url, filename: r.filename, uploading: false });
      toast.success('写真を添付しました');
    } catch (e) {
      patchLine(i, { uploading: false });
      toast.error(e?.response?.data?.detail || '写真のアップロードに失敗しました');
    }
  };

  const openMailToSupplier = useCallback(({ order: o, discrepancies }) => {
    const to = (o.supplier_snapshot || {}).email || o.supplier_email || '';
    const shortLines = (discrepancies || []).map((d, idx) => (
      `${idx + 1}. ${d.name || d.part_no || `明細 ${d.line_index + 1}`}` +
      ` — 発注 ${d.ordered}, 今回入荷 ${d.received_this_time}, 累計 ${d.received_total}, 残 ${d.remaining}` +
      (d.discrepancy_note ? `\n   備考: ${d.discrepancy_note}` : '')
    )).join('\n');
    const subject = `【入荷不足のお知らせ】${o.order_no} — ${(o.supplier_snapshot || {}).name || ''}`;
    const body = [
      `お世話になっております。`,
      ``,
      `下記の発注 (${o.order_no}) について、本日の入荷で数量不足が確認されました。`,
      `お手数をお掛けしますが、ご確認と対応のご連絡をお願いいたします。`,
      ``,
      `── 不足明細 ──`,
      shortLines,
      ``,
      `── 発注情報 ──`,
      `発注No: ${o.order_no}`,
      `納期: ${o.due_date || '—'}`,
      `合計: ¥${YEN.format(o.total || 0)}`,
      ``,
      `以上、宜しくお願いいたします。`,
    ].join('\n');
    const url = `mailto:${encodeURIComponent(to)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = url;
  }, []);

  const submit = async () => {
    if (!order) return;
    const lines = Object.entries(lineState)
      .filter(([, s]) => Number(s?.qty) > 0)
      .map(([i, s]) => ({
        line_index: Number(i),
        received_qty: Number(s.qty),
        photo_url: s.photo_url || null,
        discrepancy_note: s.discrepancy_note?.trim() || null,
      }));
    if (!lines.length) { toast.error('入荷数量を1件以上入力してください'); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API_ROOT}/shizai/orders/${order.id}/receive`, {
        lines, note: note || null,
      });
      toast.success('入荷を記録しました', {
        description: `${order.order_no} · ${r.data.status_label || r.data.status}`,
      });
      scanCommitFeedback('stock_in');
      // Discrepancy modal — only when the backend reports partial lines
      const dscs = r.data.discrepancies || [];
      if (dscs.length > 0) {
        setDiscrepancy({ order: r.data, discrepancies: dscs });
      } else {
        clear();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || '入荷登録に失敗しました');
      scanFeedback(false);
    } finally { setBusy(false); }
  };

  const totalReceive = useMemo(
    () => Object.values(lineState).reduce((a, s) => a + (Number(s?.qty) || 0), 0),
    [lineState],
  );

  return (
    <div className="min-h-screen bg-zinc-100 pb-[128px] md:pb-24" data-testid="scanner-po-receive">
      {/* Sticky header */}
      <header className="sticky top-0 z-10 bg-white border-b border-zinc-200 px-3 py-2.5 flex items-center gap-2 shadow-sm">
        <button onClick={() => navigate('/scanner')} className="p-1 rounded hover:bg-zinc-100"
          data-testid="po-recv-back">
          <ArrowLeft size={20} />
        </button>
        <Truck size={20} weight="duotone" className="text-emerald-500" />
        <h1 className="text-base font-bold flex-1">PO 検収</h1>
        {order && (
          <button onClick={clear} className="p-1 rounded hover:bg-zinc-100 text-zinc-500" title="別の発注をスキャン"
            data-testid="po-recv-clear">
            <X size={18} />
          </button>
        )}
      </header>

      <div className="p-3 space-y-3">
        {!order ? (
          <>
            {/* Scanner / manual input */}
            <div className="bg-white rounded-xl border border-zinc-200 shadow-sm p-3 space-y-3">
              <Label className="text-xs font-semibold text-zinc-600">発注No. をスキャンまたは入力</Label>
              <div className="flex gap-2">
                <Input
                  ref={inputRef}
                  value={orderNo}
                  onChange={(e) => setOrderNo(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && lookup(orderNo)}
                  placeholder="1000000084"
                  data-testid="po-recv-input"
                  className="h-11 text-base font-mono"
                  autoFocus
                />
                <Button onClick={() => lookup(orderNo)} disabled={busy || !orderNo.trim()}
                  data-testid="po-recv-lookup"
                  className="h-11 bg-teal-700 hover:bg-teal-800">
                  {busy ? '検索中…' : '検索'}
                </Button>
              </div>
              <div className="border-t border-zinc-200 pt-3">
                <ScannerScanner onDetected={onDetected} />
              </div>
            </div>
            {/* Empty state hint */}
            <div className="bg-white rounded-xl border border-zinc-200 p-6 text-center text-sm text-zinc-500">
              <Barcode size={40} weight="duotone" className="text-zinc-300 mx-auto mb-2" />
              <p>発注書のバーコード（発注No.）をスキャンしてください。</p>
              <p className="text-xs mt-1">対応形式: Code128 / QR / DataMatrix / EAN / UPC / ITF</p>
              <p className="text-xs mt-1">連続オートフォーカス対応・カメラが起動しない場合は上フィールドに手入力できます。</p>
            </div>
          </>
        ) : (
          <>
            {/* PO summary card */}
            <div className="bg-white rounded-xl border border-zinc-200 shadow-sm p-3" data-testid="po-recv-summary">
              <div className="flex items-baseline justify-between mb-1">
                <span className="font-mono text-lg font-bold text-emerald-600">{order.order_no}</span>
                <Badge variant="outline" className="text-[10px]">
                  {order.status_label || order.status}
                </Badge>
              </div>
              <p className="text-sm text-zinc-700 font-medium truncate">{order.supplier_name || (order.supplier_snapshot || {}).name || '—'}</p>
              <div className="mt-1 text-xs text-zinc-500 flex items-center gap-3 flex-wrap">
                {order.due_date && <span>納期: {order.due_date}</span>}
                <span className="tabular-nums">合計: ¥{YEN.format(order.total || 0)}</span>
                <span className="tabular-nums">明細: {(order.lines || []).length}件</span>
              </div>
            </div>

            {/* Fill all button */}
            <Button onClick={fillAllRemaining} variant="outline"
              data-testid="po-recv-fill-all"
              className="w-full h-11 border-emerald-300 text-emerald-700 hover:bg-emerald-50">
              <CheckCircle size={16} className="mr-1.5" weight="duotone" />
              全ての残数量を一括入力
            </Button>

            {/* Line items */}
            <div className="space-y-2" data-testid="po-recv-lines">
              {(order.lines || []).map((line, i) => {
                const st = lineState[i] || {};
                const already = line.received_qty || 0;
                const ordered = line.qty || 0;
                const remaining = Math.max(0, ordered - already);
                const receiving = Number(st.qty || 0);
                const fullyReceived = already >= ordered;
                const isShort = receiving > 0 && receiving < remaining;
                return (
                  <div key={`${line.item_id || line.name || 'line'}-${i}`} className={`bg-white rounded-xl border p-3 ${fullyReceived ? 'opacity-60' : (isShort ? 'border-amber-300 ring-1 ring-amber-100' : 'border-zinc-200')}`}
                    data-testid={`po-recv-line-${i}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-zinc-800 truncate">{line.name || line.part_no || '—'}</p>
                        {(line.part_no || line.drawing_no) && (
                          <p className="text-[11px] text-zinc-500 font-mono truncate">
                            {[line.part_no, line.drawing_no].filter(Boolean).join(' / ')}
                          </p>
                        )}
                      </div>
                      {fullyReceived && (
                        <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px] shrink-0"
                          data-testid={`po-recv-line-full-${i}`}>
                          <CheckCircle size={10} weight="fill" className="mr-0.5" />
                          全数入荷済
                        </Badge>
                      )}
                    </div>

                    {/* Qty info */}
                    <div className="mt-2 grid grid-cols-3 gap-2 text-[11px]">
                      <div className="bg-zinc-50 rounded p-1.5 text-center">
                        <div className="text-zinc-500">発注数</div>
                        <div className="font-bold tabular-nums text-zinc-800">{ordered} {line.uom}</div>
                      </div>
                      <div className="bg-emerald-50 rounded p-1.5 text-center">
                        <div className="text-emerald-600">入荷済</div>
                        <div className="font-bold tabular-nums text-emerald-700">{already}</div>
                      </div>
                      <div className={`rounded p-1.5 text-center ${isShort ? 'bg-amber-50' : 'bg-amber-50'}`}>
                        <div className="text-amber-600">残数</div>
                        <div className="font-bold tabular-nums text-amber-700">{remaining}</div>
                      </div>
                    </div>

                    {/* Qty picker */}
                    {!fullyReceived && (
                      <>
                        <div className="mt-2 flex items-center gap-2">
                          <button
                            onClick={() => setLineQty(i, receiving - 1)}
                            disabled={receiving <= 0}
                            data-testid={`po-recv-decr-${i}`}
                            className="p-2 rounded-full bg-zinc-100 disabled:opacity-30 hover:bg-zinc-200 active:bg-zinc-300"
                          >
                            <MinusCircle size={24} weight="duotone" />
                          </button>
                          <Input
                            type="number" min={0} max={remaining} step={1}
                            value={receiving}
                            onChange={(e) => setLineQty(i, e.target.value)}
                            data-testid={`po-recv-qty-${i}`}
                            className="h-12 text-center text-lg font-bold tabular-nums"
                          />
                          <button
                            onClick={() => setLineQty(i, receiving + 1)}
                            disabled={receiving >= remaining}
                            data-testid={`po-recv-incr-${i}`}
                            className="p-2 rounded-full bg-zinc-100 disabled:opacity-30 hover:bg-zinc-200 active:bg-zinc-300"
                          >
                            <PlusCircle size={24} weight="duotone" />
                          </button>
                          <Button size="sm" variant="outline" onClick={() => fillRemaining(i)}
                            data-testid={`po-recv-fill-${i}`}
                            className="h-11 text-xs shrink-0 border-emerald-300 text-emerald-700 hover:bg-emerald-50">
                            残り {remaining}
                          </Button>
                        </div>

                        {/* Per-line photo + short-receipt note */}
                        {receiving > 0 && (
                          <div className="mt-2 space-y-2 border-t border-zinc-200 pt-2">
                            {/* Photo attachment */}
                            <div className="flex items-center gap-2">
                              <input
                                type="file"
                                accept="image/*"
                                capture="environment"
                                ref={(el) => { fileRefs.current[i] = el; }}
                                onChange={(e) => uploadPhoto(i, e.target.files?.[0])}
                                className="hidden"
                                data-testid={`po-recv-photo-input-${i}`}
                              />
                              {st.photo_url ? (
                                <div className="flex-1 flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded px-2 py-1.5">
                                  <img
                                    src={`${process.env.REACT_APP_BACKEND_URL}${st.photo_url}`}
                                    alt="proof"
                                    className="w-10 h-10 rounded object-cover shrink-0"
                                    data-testid={`po-recv-photo-thumb-${i}`}
                                  />
                                  <div className="flex-1 min-w-0">
                                    <div className="text-[10px] text-emerald-700 font-semibold">写真添付済</div>
                                    <div className="text-[10px] text-zinc-500 truncate">{st.filename || 'photo.jpg'}</div>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => patchLine(i, { photo_url: '', filename: '' })}
                                    data-testid={`po-recv-photo-clear-${i}`}
                                    className="p-1 rounded hover:bg-red-50 text-red-500"
                                  >
                                    <Trash size={13} />
                                  </button>
                                </div>
                              ) : (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  disabled={st.uploading}
                                  onClick={() => fileRefs.current[i]?.click()}
                                  data-testid={`po-recv-photo-btn-${i}`}
                                  className="flex-1 h-9 border-blue-300 text-blue-700 hover:bg-blue-50"
                                >
                                  {st.uploading ? (
                                    <><ArrowsClockwise size={12} className="mr-1.5 animate-spin" />アップロード中…</>
                                  ) : (
                                    <><Camera size={13} className="mr-1.5" weight="duotone" />写真を添付（証跡）</>
                                  )}
                                </Button>
                              )}
                            </div>

                            {/* Discrepancy note (only shown when short-receipt) */}
                            {isShort && (
                              <div className="bg-amber-50 border border-amber-200 rounded p-2" data-testid={`po-recv-short-${i}`}>
                                <div className="text-[10px] text-amber-800 font-semibold mb-1 flex items-center gap-1">
                                  <Warning size={11} weight="fill" className="text-amber-700" />
                                  数量不足: 発注残 {remaining} / 今回 {receiving} → 不足 {remaining - receiving}
                                </div>
                                <Input
                                  value={st.discrepancy_note || ''}
                                  onChange={(e) => patchLine(i, { discrepancy_note: e.target.value })}
                                  placeholder="原因メモ（例: 破損 / 未着 / 別送予定 / 未検品）"
                                  data-testid={`po-recv-short-note-${i}`}
                                  className="h-9 text-xs"
                                />
                              </div>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Note */}
            <div>
              <Label className="text-xs font-semibold text-zinc-600">全体備考（任意）</Label>
              <Input value={note} onChange={(e) => setNote(e.target.value)}
                data-testid="po-recv-note"
                placeholder="現品票の状態、担当者名など"
                className="h-10 mt-1" />
            </div>
          </>
        )}
      </div>

      {/* Sticky bottom action bar — the global BottomTabBar is hidden on
          scanner routes (Feb 2026 redesign), so we anchor at bottom-0 with
          safe-area padding on all breakpoints. */}
      {order && (
        <div className="fixed left-0 right-0 bottom-0 bg-white border-t border-zinc-200 shadow-lg p-3 flex items-center gap-2 z-40 pb-[env(safe-area-inset-bottom)]">
          <div className="flex-1">
            <p className="text-[10px] text-zinc-500">合計入荷数量</p>
            <p className="text-xl font-bold tabular-nums text-emerald-600" data-testid="po-recv-total">{totalReceive}</p>
          </div>
          <Button onClick={submit} disabled={busy || totalReceive === 0}
            data-testid="po-recv-submit"
            className="h-12 px-6 bg-teal-700 hover:bg-teal-800 text-base font-bold shadow-md">
            {busy ? (
              <><ArrowsClockwise size={16} className="mr-1 animate-spin" /> 送信中…</>
            ) : (
              <><CheckCircle size={18} className="mr-1.5" weight="duotone" /> 入荷を確定</>
            )}
          </Button>
        </div>
      )}

      {/* Post-submit discrepancy modal */}
      <Dialog open={!!discrepancy} onOpenChange={(open) => { if (!open) { setDiscrepancy(null); clear(); } }}>
        <DialogContent className="max-w-md" data-testid="po-recv-discrepancy-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              <Warning size={22} weight="fill" />
              数量不足レポート
            </DialogTitle>
            <DialogDescription>
              入荷未達の明細を確認し、仕入先に連絡してください。
            </DialogDescription>
          </DialogHeader>
          {discrepancy && (
            <div className="space-y-3">
              <p className="text-xs text-zinc-500">
                発注 <b className="font-mono text-emerald-600">{discrepancy.order.order_no}</b> に、まだ入荷未達の明細が {discrepancy.discrepancies.length} 件あります。
              </p>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {discrepancy.discrepancies.map((d, idx) => (
                  <div key={d.line_index ?? idx}
                    data-testid={`po-recv-discrepancy-item-${d.line_index}`}
                    className="bg-amber-50 border border-amber-200 rounded p-2">
                    <div className="flex items-center gap-2">
                      <Package size={14} weight="duotone" className="text-amber-700 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-zinc-800 truncate">{d.name || d.part_no || `明細 ${d.line_index + 1}`}</div>
                        <div className="text-[10px] text-zinc-500 tabular-nums">
                          発注 {d.ordered} / 今回 {d.received_this_time} / 累計 {d.received_total} / <b className="text-amber-700">残 {d.remaining}</b>
                        </div>
                        {d.discrepancy_note && (
                          <div className="text-[10px] text-amber-800 mt-0.5">📝 {d.discrepancy_note}</div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="pt-2 border-t border-zinc-200">
                <p className="text-[10px] text-zinc-500 mb-1.5">
                  仕入先: <b>{(discrepancy.order.supplier_snapshot || {}).name || '—'}</b>
                </p>
                <Button
                  onClick={() => openMailToSupplier(discrepancy)}
                  data-testid="po-recv-mail-supplier"
                  className="w-full h-11 bg-blue-500 hover:bg-blue-600"
                >
                  <EnvelopeOpen size={16} className="mr-1.5" weight="duotone" />
                  仕入先に連絡（メール下書き）
                </Button>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setDiscrepancy(null); clear(); }}
              data-testid="po-recv-discrepancy-close"
              className="w-full"
            >
              閉じる
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
